import { FF_ENABLE_SHOPIFY_PAYMENT } from '@/constants/featureFlags';
import { useFeatureFlags } from '../flags';
import type { GetPaymentStatusQuery } from '../graphql/generated/graphql';

export type ConsultPaymentMethod = 'INVOICE' | 'CREDIT_CARD';

export enum ConsultPaymentStateEnum {
  NotPaid = 'NOT_PAID',
  InProgress = 'IN_PROGRESS',
  Paid = 'PAID',
}
export type ConsultPaymentState = `${ConsultPaymentStateEnum}`;

export type ConsultPaymentStatus = GetPaymentStatusQuery['getPaymentStatus']['paymentStatus'];

export type ConsultPaymentStatusData = GetPaymentStatusQuery['getPaymentStatus'];

// this is in reference to a consultations payment_status which is different to the paymentStatus that is in the payload
export type ConsultationPaymentStatus = 'INVOICED' | 'PAID' | 'REFUNDED' | null;

function derivePaymentMethod(paymentDetails?: ConsultPaymentStatusData): ConsultPaymentMethod {
  if (paymentDetails?.preAuthTransactionExists) {
    return 'CREDIT_CARD';
  }
  return 'INVOICE';
}

function deriveCreditCardPaymentState(
  paymentDetails?: ConsultPaymentStatusData,
  paymentQueued?: boolean,
  paymentStatus?: ConsultationPaymentStatus,
): ConsultPaymentState {
  if (paymentDetails?.debitTransactionExists || paymentStatus === 'PAID') {
    return 'PAID';
  }
  if (paymentQueued) {
    return 'IN_PROGRESS';
  }
  return 'NOT_PAID';
}

function deriveInvoicePaymentState(paidDate?: string, invoiceCode?: string): ConsultPaymentState {
  if (paidDate) {
    return 'PAID';
  }
  if (invoiceCode) {
    return 'IN_PROGRESS';
  }
  return 'NOT_PAID';
}

/**
 * Derives the payment state for a consultation based on a payment status.
 * This function maps the payment status to a standardized payment state used in the ConsultationTab.
 *
 * @param paymentStatus - The current payment status of the consultation
 * @param paidDate - The date the consultation was paid
 * @param invoiceCode - The code of the xero invoice for the consultation indicating a consultation has been invoiced
 * @returns The derived payment state:
 *   - 'PAID' if payment status is 'PAID' or the consultation has a paid date
 *   - 'IN_PROGRESS' if payment status is 'INVOICED' or the consultation has an invoice code
 *   - 'NOT_PAID' for any other status or undefined
 */
function deriveShopifyPaymentState(
  paymentStatus?: ConsultationPaymentStatus,
  paidDate?: string,
  invoiceCode?: string,
): ConsultPaymentState {
  if (paymentStatus === 'PAID' || paymentStatus === 'REFUNDED' || paidDate) {
    return 'PAID';
  }
  if (paymentStatus === 'INVOICED' || invoiceCode) {
    return 'IN_PROGRESS';
  }
  return 'NOT_PAID';
}

function derivePaymentState(
  paymentMethod: ConsultPaymentMethod,
  paymentDetails?: ConsultPaymentStatusData,
  paymentQueued?: boolean,
  paidDate?: string, // e.g. 2021-05-19T00:00:00.000Z,
  invoiceCode?: string,
  paymentStatus?: ConsultationPaymentStatus,
  isShopifyCheckoutEnabled?: boolean,
): ConsultPaymentState {
  if (paymentMethod === 'CREDIT_CARD') {
    return deriveCreditCardPaymentState(paymentDetails, paymentQueued, paymentStatus);
  }
  if (isShopifyCheckoutEnabled) {
    return deriveShopifyPaymentState(paymentStatus, paidDate, invoiceCode);
  }
  return deriveInvoicePaymentState(paidDate, invoiceCode);
}

// Mark everything as optional for now since this hook is consumed in a js page that optionally displays a specific
// consult.
export type UseConsultProps = Partial<{
  paymentStatus: ConsultationPaymentStatus;
  paymentDetails: ConsultPaymentStatusData;
  paymentQueued: boolean;
  paidDate: string;
  invoiceCode: string;
}>;

//TODO: deprecate this hook when we remove the shopify payment feature flag. Use usePaymentState instead
// A hook for managing a singular consultations state. This hook will help us separate the "showConsultation"
// component block from of the ConsultationTab.
export function useConsult({ paymentStatus, paymentDetails, paymentQueued, paidDate, invoiceCode }: UseConsultProps) {
  const { flags } = useFeatureFlags();
  const ffEnableShopifyPayment = flags[FF_ENABLE_SHOPIFY_PAYMENT];
  const paymentMethod = derivePaymentMethod(paymentDetails);

  const paymentState = derivePaymentState(
    paymentMethod,
    paymentDetails,
    paymentQueued,
    paidDate,
    invoiceCode,
    paymentStatus,
    ffEnableShopifyPayment,
  );

  return {
    paymentMethod,
    paymentState,
  };
}

export default useConsult;
